import React, { useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";
import { useAuth } from "../../contexts/AuthContext";
import { useLoginModal } from "../../contexts/LoginModalContext";

interface ProtectedRouteProps {
  children: React.ReactNode;
}

// Check if we're in development mode
const isDevelopment =
  process.env.NODE_ENV === "development" ||
  process.env.REACT_APP_DISABLE_AUTH === "true";

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  const { openModal } = useLoginModal();

  // Call hooks before any early returns
  useEffect(() => {
    // Only run this effect if not in development mode
    if (!isDevelopment && !isLoading && !isAuthenticated) {
      // Store intended route and open login modal
      openModal(location.pathname);
    }
  }, [isAuthenticated, isLoading, location.pathname, openModal]);

  // In development mode, bypass authentication
  if (isDevelopment) {
    return <>{children}</>;
  }

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}
